@file:OptIn(ExperimentalSerializationApi::class)
package com.gsb.vyapar.bhagya.domain

import com.gsb.vyapar.bhagya.datasource.network.DtoId
import com.gsb.vyapar.bhagya.presentation.auth.util.isUserAdmin
import com.gsb.vyapar.bhagya.util.fromU
import com.gsb.vyapar.bhagya.util.toM
import io.ktor.util.decodeBase64Bytes
import io.ktor.util.encodeBase64
import korlibs.io.compression.compress
import korlibs.io.compression.deflate.GZIP
import korlibs.io.compression.uncompress
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromByteArray
import kotlinx.serialization.encodeToByteArray
import kotlinx.serialization.protobuf.ProtoBuf

@Serializable
data class Business(
    val v: Int = 0,
    val i: Int = 0,
    val u: String = "",
    val d: D = D()
) {
    @Serializable
    data class D(
        val data: Map<String, String> = mapOf()
    )
}

fun DtoId.toBusiness(): Business {
    return Business(v= v, i = i, u = u, d = ProtoBuf.decodeFromByteArray<Business.D>(d.decodeBase64Bytes().uncompress(GZIP)))
}

fun Business.toDtoId(): DtoId {
    return DtoId(v = v, i = i, u = u, d = ProtoBuf.encodeToByteArray(d).compress(GZIP).encodeBase64())
}

fun String.decodeBusinessString(): Business {
    return try {
        if ("H4sIAAAAAAAAAAAAAAAAAAAA" == this) {
            return Business()
        }
        ProtoBuf.decodeFromByteArray<Business>(this.decodeBase64Bytes().uncompress(GZIP))
    } catch (e: Exception) {
        Business()
    }
}

fun Business.encodeBusinessString(): String {
    return ProtoBuf.encodeToByteArray(this).compress(GZIP).encodeBase64()
}

fun Business.getId(): String {
    return this.d.data[ID].orEmpty()
}

fun Business.getTempId(): String {
    return this.d.data[TEMP_ID].orEmpty()
}

fun Business.getUserId(): String {
    return this.d.data[USERID].orEmpty()
}

fun Business.getCreated(): String {
    return this.d.data[CREATED].orEmpty()
}

fun Business.getUpdated(): String {
    return this.d.data[UPDATED].orEmpty()
}

fun Business.getName(): String {
    return this.d.data[NAME].orEmpty()
}

fun Business.getDisplayName(): String {
    if (isUserAdmin()) {
        return this.getName() + " (" + this.getRegistrationNumber() + ")" + " id: ${this.getId().fromU()} user: ${this.getUserId()}"
    } else {
        return this.getName() + " (" + this.getRegistrationNumber() + ")"
    }
}

fun Business.getDescription(): String {
    return this.d.data[DESCRIPTION].orEmpty()
}

fun Business.getServices(): String {
    return this.d.data[SERVICE].orEmpty()
}

fun Business.getCategoryId(): String {
    return this.d.data[CATEGORY_ID].orEmpty()
}

fun Business.getSubCategoryIds(): String {
    return this.d.data[SUB_CATEGORY_IDS].orEmpty()
}

fun Business.getPincode(): String {
    return this.d.data[PINCODE].orEmpty()
}

fun Business.getAddress(): String {
    return this.d.data[ADDRESS].orEmpty()
}

fun Business.getLatitude(): String {
    return this.d.data[LATITUDE].orEmpty()
}

fun Business.getLongitude(): String {
    return this.d.data[LONGITUDE].orEmpty()
}

fun Business.getLogo(): String {
    return this.d.data[LOGO].orEmpty()
}

fun Business.getCover(): String {
    return this.d.data[COVER].orEmpty()
}

fun Business.getImage1(): String {
    return this.d.data[IMAGE1].orEmpty()
}

fun Business.getImage2(): String {
    return this.d.data[IMAGE2].orEmpty()
}

fun Business.getImage3(): String {
    return this.d.data[IMAGE3].orEmpty()
}

fun Business.getImage4(): String {
    return this.d.data[IMAGE4].orEmpty()
}

fun Business.getImage5(): String {
    return this.d.data[IMAGE5].orEmpty()
}

fun Business.getEmail(): String {
    return this.d.data[EMAIL].orEmpty()
}

fun Business.getPhone1(): String {
    return this.d.data[PHONE1].orEmpty()
}

fun Business.getPhone2(): String {
    return this.d.data[PHONE2].orEmpty()
}

fun Business.getMap(): String {
    return this.d.data[MAP].orEmpty()
}

fun Business.getExtra(): String {
    return this.d.data[EXTRA].orEmpty()
}

fun Business.getYoutube(): String {
    return this.d.data[YOUTUBE].orEmpty()
}

fun Business.getLinkedIn(): String {
    return this.d.data[LINKEDIN].orEmpty()
}

fun Business.getWebsite(): String {
    return this.d.data[WEBSITE].orEmpty()
}

fun Business.getFacebook(): String {
    return this.d.data[FACEBOOK].orEmpty()
}

fun Business.getInstagram(): String {
    return this.d.data[INSTAGRAM].orEmpty()
}

fun Business.getX(): String {
    return this.d.data[X].orEmpty()
}

fun Business?.getIndex(): Int {
    return this?.d?.data?.get(ID).orEmpty().fromU() ?: 0
}

fun Business.showSocialLinks(): Boolean {
    return getFacebook().isNotEmpty() || getInstagram().isNotEmpty() || getX().isNotEmpty() || getLinkedIn().isNotEmpty() || getYoutube().isNotEmpty()
}

fun Business.showGallery(): Boolean {
    return getImage1().isNotEmpty() || getImage2().isNotEmpty() || getImage3().isNotEmpty() || getImage4().isNotEmpty() || getImage5().isNotEmpty()
}

fun Business.getImages(): List<String> {
    return listOf<String>() + getImage1() + getImage2() + getImage3() + getImage4() + getImage5()
}

fun Business.getGalleryPage(galleryPage: Int) {
    val size = getImages().size
}

fun Business.getRegistrationNumber(): String {
    return "GSB-VB-${this.d.data[ID].orEmpty()}"
}

fun Business.isBusinessDeleted(): Boolean {
    return this.d.data[DELETED] == DELETED
}